import React, { useMemo, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import MonacoEditor from "react-monaco-editor";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import { formatCode, isFormattable } from "../lib/formatCode";
import "./Styles.css";
import {
  Eraser,
  Copy,
  WrapText,
  AArrowDown,
  AArrowUp,
  X,
  Focus,
  Download,
  Wand2,
} from "lucide-react";

const CodeEditor = ({ label, code, onChange, onClear, onCopy }) => {
  const [wordWrap, setWordWrap] = useState(false);
  const [fontSize, setFontSize] = useState(14);
  const [formatting, setFormatting] = useState(false);
  const language = label.toLowerCase();
  const formattable = isFormattable(language);

  const toggleWordWrap = () => {
    setWordWrap(!wordWrap);
  };

  const increaseFontSize = () => {
    setFontSize(fontSize + 1);
  };

  const decreaseFontSize = () => {
    setFontSize(fontSize - 1);
  };

  const handleFormat = async () => {
    if (!formattable || formatting) return;
    setFormatting(true);
    try {
      const formatted = await formatCode(code, language);
      onChange(formatted);
    } catch (err) {
      toast.error(`Could not format ${label} — check for syntax errors.`);
    } finally {
      setFormatting(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-sm border border-vscode-border bg-vscode-panel">
      <div className="flex w-full items-center justify-between border-b border-vscode-border px-3 py-1.5">
        <label className="text-xs font-semibold uppercase tracking-wide text-vscode-textMuted">
          {label}
        </label>

        <div className="flex gap-1 text-vscode-textMuted" id="options">
          <button
            onClick={handleFormat}
            disabled={!formattable || formatting}
            title="Format Document"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Wand2 size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={increaseFontSize}
            title="Increase Font Size"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <AArrowUp size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={decreaseFontSize}
            title="Decrease Font Size"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <AArrowDown size={16} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => onCopy(code)}
            title="Copy"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <Copy size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={toggleWordWrap}
            title="Toggle Word Wrap"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-textBright"
          >
            <WrapText size={14} strokeWidth={1.5} />
          </button>
          <button
            onClick={onClear}
            title="Clear"
            className="rounded-sm p-1 hover:bg-vscode-listHover hover:text-vscode-red"
          >
            <Eraser size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <MonacoEditor
        height="25vh"
        width={`100%`}
        language={label.toLowerCase()}
        value={code}
        onChange={onChange}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: fontSize,
          fontFamily: "'Fira Code', Consolas, 'Courier New', monospace",
          fontLigatures: true,
          wordWrap: wordWrap ? "on" : "off",
        }}
      />
    </div>
  );
};

const SvgEditor = () => {
  const [svgCode, setSvgCode] = useState("");
  const [fileName, setFileName] = useState("Image.svg");

  const sanitizedSvg = useMemo(
    () =>
      DOMPurify.sanitize(svgCode, {
        USE_PROFILES: { svg: true, svgFilters: true },
      }),
    [svgCode]
  );

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();

    reader.onload = (e) => {
      setSvgCode(e.target.result);
    };

    reader.readAsText(file);
    // Reset so selecting the same file again still fires onChange.
    event.target.value = "";
  };

  const handleClear = () => {
    setSvgCode("");
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleFileNameChange = (event) => {
    setFileName(event.target.value);
  };

  const handleDownload = () => {
    const file = new Blob([svgCode], { type: "image/svg+xml" });
    const url = URL.createObjectURL(file);
    const element = document.createElement("a");
    element.href = url;
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
  };

  return (
    <TransformWrapper initialScale={1} initialPositionX={0} initialPositionY={0}>
      {({ zoomIn, zoomOut, resetTransform, centerView }) => (
        <div className="flex h-full w-full flex-col gap-3 bg-vscode-bg p-4 lg:flex-row">
          <div className="flex w-full flex-col gap-3 lg:w-1/2">
            <div className="h-64 lg:h-[45vh]">
              <CodeEditor
                label="SVG"
                code={svgCode}
                onChange={setSvgCode}
                onClear={handleClear}
                onCopy={handleCopy}
              />
            </div>
            <div className="flex flex-col gap-2 rounded-sm border border-vscode-border bg-vscode-panel p-3">
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="file"
                  accept=".svg"
                  onChange={handleFileUpload}
                  className="text-xs text-vscode-textMuted file:mr-2 file:rounded-sm file:border-0 file:bg-vscode-button file:px-2 file:py-1 file:text-xs file:text-white file:hover:bg-vscode-buttonHover"
                />
                <label className="flex items-center gap-2 text-xs text-vscode-textMuted">
                  File name
                  <input
                    type="text"
                    value={fileName}
                    onChange={handleFileNameChange}
                    className="rounded-sm border border-vscode-border bg-vscode-input px-2 py-1 text-xs text-vscode-text focus:border-vscode-accent focus:outline-none"
                  />
                </label>
              </div>
              <button
                onClick={handleDownload}
                className="flex w-fit items-center gap-2 rounded-sm bg-vscode-button px-3 py-1.5 text-xs font-medium text-white hover:bg-vscode-buttonHover"
              >
                <Download size={14} strokeWidth={1.5} />
                Download SVG
              </button>
            </div>
          </div>
          <div className="flex w-full flex-col lg:w-1/2">
            <div className="flex items-center justify-end gap-1 rounded-t-sm border border-b-0 border-vscode-border bg-vscode-panel px-3 py-1.5 text-vscode-textMuted">
              <button
                onClick={() => zoomIn()}
                title="Zoom In"
                className="rounded-sm p-1.5 hover:bg-vscode-listHover hover:text-vscode-textBright"
              >
                <ZoomIn size={16} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => zoomOut()}
                title="Zoom Out"
                className="rounded-sm p-1.5 hover:bg-vscode-listHover hover:text-vscode-textBright"
              >
                <ZoomOut size={16} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => resetTransform()}
                title="Reset"
                className="rounded-sm p-1.5 hover:bg-vscode-listHover hover:text-vscode-textBright"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => centerView()}
                title="Go to center"
                className="rounded-sm p-1.5 hover:bg-vscode-listHover hover:text-vscode-textBright"
              >
                <Focus size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="svg-checkerboard flex-1 overflow-hidden rounded-b-sm border border-vscode-border">
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
                  className="flex h-full w-full items-center justify-center"
                />
              </TransformComponent>
            </div>
          </div>
        </div>
      )}
    </TransformWrapper>
  );
};

export default SvgEditor;
